(() => {
  class BEKShadingEngine {
    constructor(data, options = {}) {
      this.data = data || {};
      const p = this.data.pricing || {};
      this.options = {
        vatPct: Number(options.vatPct ?? p.vatPct ?? 19),
        dealerDiscountPct: Number(options.dealerDiscountPct ?? p.dealerDiscountPct ?? 50),
        targetMarginPct: Number(options.targetMarginPct ?? p.targetMarginPct ?? 40),
        priceMode: options.priceMode || p.priceMode || 'vd_list_round90',
        roundingMode: options.roundingMode || p.roundingMode || 'x090'
      };
    }
    ceiling(value, bands) {
      const n = Number(value);
      if (!Number.isFinite(n)) return null;
      return (bands || []).find((v) => n <= Number(v)) ?? null;
    }
    roundUpTo90(value) { const n=Number(value); return Number.isFinite(n) ? Math.ceil((n+10)/100)*100-10 : 0; }
    roundUpTo10(value) { const n=Number(value); return Number.isFinite(n) ? Math.ceil(n/10)*10 : 0; }
    applyRound(value) {
      if (this.options.roundingMode === 'x010') return this.roundUpTo10(value);
      if (this.options.roundingMode === 'euro') return Math.round(Number(value)||0);
      return this.roundUpTo90(value);
    }
    optionalExtras({ sensorPackage='NONE', tahoma=false, extraRemote=false } = {}) {
      const catalog=this.data.pricing?.optionalAccessoriesNetList || {};
      const allowedSensors=['NONE','WIND_EOLIS','SUN_SUNIS','WIND_SUN_SOLIRIS'];
      const sensor=allowedSensors.includes(String(sensorPackage||'NONE').toUpperCase())?String(sensorPackage||'NONE').toUpperCase():'NONE';
      const codes=[];
      if(sensor!=='NONE')codes.push(sensor);
      if(Boolean(tahoma))codes.push('TAHOMA');
      if(Boolean(extraRemote))codes.push('EXTRA_REMOTE');
      const items=codes.map(code=>({code,label:catalog?.[code]?.label||code,netList:Number(catalog?.[code]?.price||0)}));
      return {sensorPackage:sensor,tahoma:Boolean(tahoma),extraRemote:Boolean(extraRemote),items,netList:items.reduce((a,x)=>a+x.netList,0)};
    }
    finish({ family, model, requestedWidth, requestedSecond, secondName, widthBand, secondBand, baseListNet, mandatoryListNet, ledListNet = 0, optionalExtras={}, color, labels = {} }) {
      const extras=this.optionalExtras(optionalExtras);
      const vdListNet = Number(baseListNet) + Number(mandatoryListNet||0) + Number(ledListNet||0) + Number(extras.netList||0);
      const bekEkNet = vdListNet * (1 - this.options.dealerDiscountPct/100);
      let customerGross;
      if (this.options.priceMode === 'target_margin') {
        const margin = this.options.targetMarginPct/100;
        customerGross = this.applyRound((bekEkNet/(1-margin))*(1+this.options.vatPct/100));
      } else customerGross = this.applyRound(vdListNet);
      const customerNet = customerGross/(1+this.options.vatPct/100);
      const grossProfitNet = customerNet-bekEkNet;
      const rawMarginPct = customerNet>0 ? grossProfitNet/customerNet*100 : 0;
      return {
        ok:true, productCode:'SHADING', family, model,
        requestedWidth:Number(requestedWidth), [secondName]:Number(requestedSecond),
        widthBand:Number(widthBand), [`${secondName}Band`]:Number(secondBand),
        color, baseListNet:Number(baseListNet), mandatoryAccessoriesNetList:Number(mandatoryListNet||0), ledNetList:Number(ledListNet||0),
        optionalAccessoriesNetList:Number(extras.netList||0), optionalAccessories:extras,
        vdListNet, dealerDiscountPct:this.options.dealerDiscountPct, bekEkNet,
        vatPct:this.options.vatPct, priceMode:this.options.priceMode, targetMarginPct:this.options.targetMarginPct,
        customerGross, customerNet, grossProfitNet, rawMarginPct,
        ...labels
      };
    }
    calculateRoof({ type='ROOF_UNDER', width, depth, color='RAL 7016 anthrazitgrau Struktur', sensorPackage='NONE', tahoma=false, extraRemote=false }) {
      const cfg=this.data.roof; if(!cfg) return {ok:false,error:'SHADING_ROOF_DATA_MISSING'};
      if(!['ROOF_UNDER','ROOF_OVER'].includes(type)) return {ok:false,error:'SHADING_TYPE_INVALID'};
      const widthBand=this.ceiling(width,cfg.widthBands), depthBand=this.ceiling(depth,cfg.depthBands);
      if(!widthBand||!depthBand) return {ok:false,error:'OUTSIDE_SHADING_ROOF_MATRIX',requestedWidth:Number(width),requestedDepth:Number(depth)};
      const key=`${widthBand}x${depthBand}`;
      const table=type==='ROOF_UNDER'?cfg.under:cfg.over;
      const baseListNet=Number(table?.pricesNetList?.[key]);
      if(!Number.isFinite(baseListNet)) return {ok:false,error:'SHADING_ROOF_PRICE_NOT_FOUND',widthBand,depthBand};
      const model=depthBand<=Number(cfg.modelRule?.lineaMaxDepthBandMm||4500)?'LINEA':'SOLARA';
      return this.finish({family:type,model,requestedWidth:width,requestedSecond:depth,secondName:'requestedDepth',widthBand,secondBand:depthBand,baseListNet,mandatoryListNet:cfg.mandatoryAccessoriesNetList,optionalExtras:{sensorPackage,tahoma,extraRemote},color,labels:{familyLabel:type==='ROOF_UNDER'?'Unterglasmarkise':'Aufglasmarkise',modelLabel:`VD ${model==='LINEA'?'Linea':'Solara'} ${type==='ROOF_UNDER'?'UZ':'AZ'}`,dimensionLabel:'Breite × Ausfall'}});
    }
    calculateArm({ model='LUNA', width, depth, color='RAL 7016 Struktur', led=false, sensorPackage='NONE', tahoma=false, extraRemote=false }) {
      const cfg=this.data.arm?.models?.[model]; if(!cfg) return {ok:false,error:'SHADING_ARM_MODEL_INVALID',model};
      const widthBand=this.ceiling(width,cfg.widthBands), depthBand=this.ceiling(depth,cfg.depthBands);
      if(!widthBand||!depthBand) return {ok:false,error:'OUTSIDE_SHADING_ARM_MATRIX',requestedWidth:Number(width),requestedDepth:Number(depth),model};
      const key=`${widthBand}x${depthBand}`; const baseListNet=Number(cfg.pricesNetList?.[key]);
      if(!Number.isFinite(baseListNet)) return {ok:false,error:'SHADING_ARM_COMBINATION_NOT_AVAILABLE',widthBand,depthBand,model};
      const ledListNet=led?Number(cfg.ledNetListByWidthBand?.[String(widthBand)]||0):0;
      return this.finish({family:'ARM',model,requestedWidth:width,requestedSecond:depth,secondName:'requestedDepth',widthBand,secondBand:depthBand,baseListNet,mandatoryListNet:cfg.mandatoryAccessoriesNetList,ledListNet,optionalExtras:{sensorPackage,tahoma,extraRemote},color,labels:{familyLabel:'Gelenkarmmarkise',modelLabel:cfg.label,dimensionLabel:'Breite × Ausfall',led:Boolean(led)}});
    }
    calculateVertical({ model='EZIP105', width, height, color='RAL 7016 anthrazitgrau Struktur', sensorPackage='NONE', tahoma=false, extraRemote=false }) {
      const root=this.data.vertical; const cfg=root?.models?.[model]; if(!cfg) return {ok:false,error:'SHADING_VERTICAL_MODEL_INVALID',model};
      const widthBand=this.ceiling(width,cfg.widthBands), heightBand=this.ceiling(height,root.heightBands);
      if(!widthBand||!heightBand) return {ok:false,error:'OUTSIDE_SHADING_VERTICAL_MATRIX',requestedWidth:Number(width),requestedHeight:Number(height),model};
      const key=`${widthBand}x${heightBand}`; const baseListNet=Number(cfg.pricesNetList?.[key]);
      if(!Number.isFinite(baseListNet)) return {ok:false,error:'SHADING_VERTICAL_COMBINATION_NOT_AVAILABLE',widthBand,heightBand,model};
      return this.finish({family:'VERTICAL',model,requestedWidth:width,requestedSecond:height,secondName:'requestedHeight',widthBand,secondBand:heightBand,baseListNet,mandatoryListNet:cfg.mandatoryAccessoriesNetList,optionalExtras:{sensorPackage,tahoma,extraRemote},color,labels:{familyLabel:'Senkrechtmarkise',modelLabel:cfg.label,dimensionLabel:'Breite × Höhe'}});
    }
    calculate(input={}) {
      const family=String(input.family||'').toUpperCase();
      if(family==='ROOF_UNDER'||family==='ROOF_OVER') return this.calculateRoof({...input,type:family});
      if(family==='ARM') return this.calculateArm(input);
      if(family==='VERTICAL') return this.calculateVertical(input);
      return {ok:false,error:'SHADING_FAMILY_INVALID',family};
    }
  }
  window.BEKShadingEngine=BEKShadingEngine;
})();
